import {
    IUsesSummary, IProFormaUses, ISourcesSummary, IWaterfallAnalysis,
    IProFormaReturnsData,
    IIncomeSummary,
    IOperatingInfo,
    IPermanentDebtInfo,
    IExpenseSummary,
    IDispositionData,
    IResidentialLotMix,
    IReportMetrics,
} from 'views/ProFormaTable/types';
import { WattsType, LOAN_RATE_TYPE } from 'views/ProFormaTable/constants';
import { numeralFormatter } from 'ns_libs/formatter';
import { ProFormaModelIds, IncomeCalculationType } from 'constants/proForma';
import { isLandDevelopmentModel } from 'helpers/proForma';
import { Section, Format, Row, RowValue } from './ExecutiveSummaryGenericTable/types';

const PROJECT_PROFIT_TOOLTIP = 'Calculated as exit value plus operating cash flow during the hold period, '
    + 'minus the total development budget (including developer fee). Unlevered.';
const PROJECT_PROFIT_MARGIN_TOOLTIP = 'Project profit divided by the total development budget';

const shouldShowPerUnitColumn = (proFormaModelId: number): boolean => {
    switch (proFormaModelId) {
        case ProFormaModelIds.APARTMENT:
        case ProFormaModelIds.APARTMENT_WITH_RETAIL:
            return true;
        case ProFormaModelIds.INDUSTRIAL:
        case ProFormaModelIds.OFFICE:
        case ProFormaModelIds.RETAIL:
        case ProFormaModelIds.DATA_CENTER:
        case ProFormaModelIds.STUDENT_HOUSING:
        default:
            return false;
    }
};

const shouldShowPerBedColumn = (proFormaModelId: number): boolean => (
    proFormaModelId === ProFormaModelIds.STUDENT_HOUSING
);

export function formatDevelopment(
    proFormaModelId: number,
    usesSummary: IUsesSummary | undefined,
    sourcesSummary: ISourcesSummary | undefined,
): Section[] {
    const sections: Section[] = [];
    const showPerUnit = shouldShowPerUnitColumn(proFormaModelId);
    const showPerBed = shouldShowPerBedColumn(proFormaModelId);
    const showPerLot = isLandDevelopmentModel(proFormaModelId);
    const showPerBuiltSf = !showPerLot;
    const columns = [
        '%',
        ...(showPerBed ? ['$ / Bed'] : []),
        ...(showPerBuiltSf ? ['$ / Built SF'] : []),
        ...(showPerLot ? ['$ / Lot'] : []),
        ...(showPerUnit ? ['$ / Unit'] : []),
        'Amount',
    ];

    if (usesSummary?.uses) {
        const totalUses = usesSummary.totals.amount || 0;
        const usesMap = new Map<number | 'root', IProFormaUses[]>();
        usesSummary.uses.forEach(use => {
            const parentId = use.parent?.id || 'root';
            if (!usesMap.has(parentId)) {
                usesMap.set(parentId, []);
            }
            usesMap.get(parentId)?.push(use);
        });

        usesMap.forEach((uses, parentId) => {
            const baseValues = (use: IProFormaUses) => [
                {
                    value: totalUses !== 0 && use.amount ? numeralFormatter((use.amount / totalUses), '0.0000') : '-',
                    format: 'percentage' as Format,
                },
                ...(showPerBed ? [{
                    value: use.costPerBed || '-',
                    format: 'currency' as Format,
                }] : []),
                ...(showPerBuiltSf ? [{
                    value: use.costPerGrossSquareFootage || '-',
                    format: 'currency-cents' as Format,
                }] : []),
                ...(showPerLot ? [{
                    value: use.costPerLot || '-',
                    format: 'currency' as Format,
                }] : []),
            ];

            const perUnitValue = (use: IProFormaUses) => ({
                value: use.costPerResidentialUnit || '-',
                format: 'currency' as Format,
            });

            const amountValue = (use: IProFormaUses) => ({
                value: use.amount || '-',
                format: 'currency' as Format,
            });

            const section: Section = {
                id: `uses-section-${parentId}`,
                title: parentId === 'root' ? 'Uses' : uses[0]?.parent?.nodeName || '',
                columns,
                rows: uses.map(use => ({
                    id: `use-${use.id}`,
                    label: use.description || '',
                    values: showPerUnit
                        ? [...baseValues(use), perUnitValue(use), amountValue(use)]
                        : [...baseValues(use), amountValue(use)],
                })),
            };
            sections.push(section);
        });

        if (usesSummary.totals) {
            const totalsBaseValues = [
                { value: 1, format: 'percentage' as Format },
                ...(showPerBed ? [{
                    value: usesSummary.totals.costPerBed || '-',
                    format: 'currency' as Format,
                }] : []),
                ...(showPerBuiltSf ? [{
                    value: usesSummary.totals.costPerGrossSquareFootage || '-',
                    format: 'currency-cents' as Format,
                }] : []),
                ...(showPerLot ? [{
                    value: usesSummary.totals.costPerLot || '-',
                    format: 'currency' as Format,
                }] : []),
            ];

            const totalsPerUnitValue = {
                value: usesSummary.totals.costPerResidentialUnit || '-',
                format: 'currency' as Format,
            };

            const totalsAmountValue = {
                value: usesSummary.totals.amount || '-',
                format: 'currency' as Format,
            };

            sections.push({
                id: 'uses-total',
                isTotal: true,
                rows: [{
                    id: 'total-uses',
                    label: 'Total uses',
                    values: showPerUnit
                        ? [...totalsBaseValues, totalsPerUnitValue, totalsAmountValue]
                        : [...totalsBaseValues, totalsAmountValue],
                }],
            });
        }
    }

    if (sourcesSummary?.sources) {
        sections.push({
            id: 'sources-section',
            title: 'Sources',
            columns: [], // Empty array to hide column headers
            rows: sourcesSummary.sources.map(source => {
                const baseValues = [
                    { value: source.sourcesPercentage || '-', format: 'percentage' as Format },
                    ...(showPerBed ? [{
                        value: source.costPerBed || '-',
                        format: 'currency' as Format,
                    }] : []),
                    ...(showPerBuiltSf ? [{ value: source.costPerGrossSquareFootage || '-', format: 'currency-cents' as Format }] : []),
                    ...(showPerLot ? [{ value: source.costPerLot || '-', format: 'currency' as Format }] : []),
                ];
                const perUnitValue = {
                    value: source.costPerResidentialUnit || '-',
                    format: 'currency' as Format,
                };
                const amountValue = { value: source.amount || '-', format: 'currency' as Format };

                return {
                    id: `source-${source.id}`,
                    label: source.name,
                    values: showPerUnit
                        ? [...baseValues, perUnitValue, amountValue]
                        : [...baseValues, amountValue],
                };
            }),
        });

        if (sourcesSummary.totals) {
            const totalsBaseValues = [
                { value: 1, format: 'percentage' as Format },
                ...(showPerBed ? [{
                    value: sourcesSummary.totals.costPerBed || '-',
                    format: 'currency' as Format,
                }] : []),
                ...(showPerBuiltSf ? [{
                    value: sourcesSummary.totals.costPerGrossSquareFootage || '-',
                    format: 'currency-cents' as Format,
                }] : []),
                ...(showPerLot ? [{
                    value: sourcesSummary.totals.costPerLot || '-',
                    format: 'currency' as Format,
                }] : []),
            ];

            const totalsPerUnitValue = {
                value: sourcesSummary.totals.costPerResidentialUnit || '-',
                format: 'currency' as Format,
            };

            const totalsAmountValue = {
                value: sourcesSummary.totals.amount || '-',
                format: 'currency' as Format,
            };

            sections.push({
                id: 'sources-total',
                isTotal: true,
                rows: [{
                    id: 'total-sources',
                    label: 'Total sources',
                    values: showPerUnit
                        ? [...totalsBaseValues, totalsPerUnitValue, totalsAmountValue]
                        : [...totalsBaseValues, totalsAmountValue],
                }],
            });
        }
    }

    return sections;
}

export function formatAssumptions(
    proFormaModelId: number,
    incomeSummary: IIncomeSummary,
    operatingInfo: IOperatingInfo,
    expenses: IExpenseSummary,
    wattsType: WattsType,
): Section[] {
    const totalValuePerSfPerYear = incomeSummary?.totalRentalIncome?.totalValuePerSfPerYear;
    const perSf = totalValuePerSfPerYear ? totalValuePerSfPerYear / 12 : '-';
    const perUnit = incomeSummary?.totalRentalIncome?.totalPricePerUnit ?? '-';
    const rentalIncomeGrowth = operatingInfo?.rentalGrowthPercentage ?? '-';
    const expensesGrowth = expenses?.totalOperatingExpenses?.totalAnnualGrowth ?? '-';

    const isApartment = ProFormaModelIds.APARTMENT === proFormaModelId;
    const isApartmentWithRetail = ProFormaModelIds.APARTMENT_WITH_RETAIL === proFormaModelId;
    const isIncomeCalculationTypeEstimate = operatingInfo?.incomeCalculationType === IncomeCalculationType.ESTIMATE;
    const areaType = isApartment || isApartmentWithRetail || isIncomeCalculationTypeEstimate ? 'SF' : 'RSF';

    let generalVacancy = '-';
    let creditLoss = '-';
    if (Array.isArray(incomeSummary?.incomeAdjustments)) {
        incomeSummary.incomeAdjustments.forEach((adj: any) => {
            if (adj.adjustmentType === 'General Vacancy') {
                generalVacancy = adj.percentOfIncome ?? '-';
            }
            if (adj.adjustmentType === 'Credit Loss') {
                creditLoss = adj.percentOfIncome ?? '-';
            }
        });
    }

    let effectiveRentRows: Row[] = [];

    if (isApartment) {
        effectiveRentRows = [
            { id: 'per-rsf', label: 'Per $/RSF/mo', values: [{ value: perSf, format: 'currency-cents' }] },
            { id: 'per-unit', label: 'Per $/unit/mo', values: [{ value: perUnit, format: 'currency' }] },
        ];
    } else if (isApartmentWithRetail) {
        const retailPerSf = incomeSummary?.totalRetailIncome?.totalValuePerSfPerYear
            ? incomeSummary.totalRetailIncome.totalValuePerSfPerYear / 12
            : '-';

        effectiveRentRows = [
            { id: 'per-rsf', label: 'Apartment $/RSF/mo', values: [{ value: perSf, format: 'currency-cents' }] },
            { id: 'retail-per-sf', label: 'Retail $/RSF/mo', values: [{ value: retailPerSf, format: 'currency-cents' }] },
            { id: 'per-unit', label: 'Apartment $/unit/mo', values: [{ value: perUnit, format: 'currency' }] },
        ];
    } else if ([ProFormaModelIds.OFFICE, ProFormaModelIds.RETAIL, ProFormaModelIds.INDUSTRIAL].includes(proFormaModelId)) {
        effectiveRentRows = [
            { id: 'per-rsf', label: '$/RSF/mo', values: [{ value: perSf, format: 'currency-cents' }] },
        ];
    } else if (proFormaModelId === ProFormaModelIds.DATA_CENTER) {
        const perKw = incomeSummary?.totalRentalIncome?.totalValuePerKwPerMonth ?? '-';
        const perMw = incomeSummary?.totalRentalIncome?.totalValuePerMwPerMonth ?? '-';
        effectiveRentRows = [
            { id: 'per-sf', label: `$/${areaType}/mo`, values: [{ value: perSf, format: 'currency-cents' }] },
            wattsType === WattsType.MEGAWATTS
                ? { id: 'per-mw', label: '$/MW/mo', values: [{ value: perMw, format: 'currency' }] }
                : { id: 'per-kw', label: '$/kW/mo', values: [{ value: perKw, format: 'currency' }] },
        ];
    } else if (proFormaModelId === ProFormaModelIds.STUDENT_HOUSING) {
        const perBed = incomeSummary?.totalRentalIncome?.totalPricePerBed ?? '-';
        effectiveRentRows = [
            { id: 'per-rsf', label: 'Per $/RSF/mo', values: [{ value: perSf, format: 'currency-cents' }] },
            { id: 'per-unit', label: 'Per $/unit/mo', values: [{ value: perUnit, format: 'currency' }] },
            { id: 'per-bed', label: 'Per $/bed/mo', values: [{ value: perBed, format: 'currency' }] },
        ];
    }     

    return [
        {
            id: 'effective-rent-section',
            title: 'Effective rent',
            columns: [''],
            rows: effectiveRentRows,
        },
        {
            id: 'annual-growth-rate-section',
            title: 'Annual growth rate',
            columns: [''],
            rows: [
                { id: 'rental-income', label: 'Rental income', values: [{ value: rentalIncomeGrowth, format: 'percentage' }] },
                { id: 'expenses', label: 'Expenses', values: [{ value: expensesGrowth, format: 'percentage' }] },
            ],
        },
        {
            id: 'income-adjustments-section',
            title: 'Income adjustments',
            columns: [''],
            rows: [
                { id: 'general-vacancy', label: 'General vacancy', values: [{ value: generalVacancy, format: 'percentage' }] },
                { id: 'credit-loss', label: 'Credit loss', values: [{ value: creditLoss, format: 'percentage' }] },
            ],
        },
    ];
}

export function formatForecast(
    disposition: IDispositionData,
    operatingInfo: IOperatingInfo,
    permanentDebtInfo: IPermanentDebtInfo,
    returnsData: IProFormaReturnsData,
): Section[] {
    const netSaleProceeds = disposition?.netSaleProceeds?.amount ?? '-';
    const permanentLoanPayoff = permanentDebtInfo?.loanPayoffAmount ?? '-';
    const equityProceeds = returnsData?.totalEquityProceedFromCapitalEvents ?? '-';

    const cashOnCashReturn = permanentDebtInfo?.cashOnCashReturn ?? '-';
    const debtServiceCoverageRatio = permanentDebtInfo?.debtServiceCoverageRatio
        ? `${numeralFormatter(permanentDebtInfo.debtServiceCoverageRatio, '0,0.00')}x`
        : '-';

    const operatingExpenseRatio = operatingInfo?.operatingExpenseRatio ?? '-';
    const breakevenOccupancy = returnsData?.breakevenOccupancy ?? '-';

    return [
        {
            id: 'forecast-section-1',
            columns: ['Value'],
            rows: [
                { id: 'net-sale-proceeds', label: 'Net sale proceeds', values: [{ value: netSaleProceeds, format: 'currency' }] },
                {
                    id: 'permanent-loan-payoff',
                    label: 'Permanent loan payoff',
                    values: [{ value: permanentLoanPayoff, format: 'currency', isNegative: true }],
                },
                {
                    id: 'equity-proceeds',
                    label: 'Equity proceeds from sale',
                    values: [
                        { value: equityProceeds, format: 'currency' },
                    ],
                },
            ],
        },
        {
            id: 'forecast-section-2',
            columns: ['Value'],
            rows: [
                { id: 'cash-on-cash-return', label: 'Cash-on-cash return', values: [{ value: cashOnCashReturn, format: 'percentage' }] },
                { id: 'debt-service-coverage-ratio', label: 'Debt service coverage ratio', values: [{ value: debtServiceCoverageRatio, format: 'none' }] },
            ],
        },
        {
            id: 'forecast-section-3',
            columns: ['Value'],
            rows: [
                { id: 'operating-expense-ratio', label: 'Operating expense ratio', values: [{ value: operatingExpenseRatio, format: 'percentage' }] },
                { id: 'breakeven-occupancy', label: 'Breakeven occupancy', values: [{ value: breakevenOccupancy, format: 'percentage' }] },
            ],
        },
    ];
}

export function formatReturns(returnsData: IProFormaReturnsData, waterfallAnalysis: IWaterfallAnalysis): Section[] {
    if (!returnsData || Object.keys(returnsData).length === 0) {
        return [];
    }

    const unleveredIrr = returnsData?.netUnleveredIrr ?? '-';
    const unleveredEmx = returnsData?.netUnleveredEquityMultiple ?? '-';
    const unleveredRoi = returnsData?.unleveredRoi ?? '-';
    const leveredIrr = returnsData?.netLeveredIrr ?? '-';
    const leveredEmx = returnsData?.netLeveredEquityMultiple ?? '-';
    const leveredNetRoi = returnsData?.leveredRoi ?? '-';

    const partnershipRows = [
        {
            id: 'lp',
            label: waterfallAnalysis?.waterfallSummary?.lpName || 'Limited Partner',
            values: [
                { value: waterfallAnalysis?.waterfallSummary?.lpNetProfit, format: 'currency' as Format },
                { value: waterfallAnalysis?.waterfallSummary?.lpRoi, format: 'percentage' as Format },
                { value: numeralFormatter(waterfallAnalysis?.waterfallSummary?.lpEmx, '0,0.00'), format: 'none' as Format, label: 'x' },
                { value: waterfallAnalysis?.waterfallSummary?.lpIrr, format: 'percentage' as Format },
            ],
        },
        {
            id: 'gp1',
            label: waterfallAnalysis?.waterfallSummary?.gp1Name || 'General Partner',
            values: [
                { value: waterfallAnalysis?.waterfallSummary?.gp1NetProfit, format: 'currency' as Format },
                { value: waterfallAnalysis?.waterfallSummary?.gp1Roi, format: 'percentage' as Format },
                { value: numeralFormatter(waterfallAnalysis?.waterfallSummary?.gp1Emx, '0,0.00'), format: 'none' as Format, label: 'x' },
                { value: waterfallAnalysis?.waterfallSummary?.gp1Irr, format: 'percentage' as Format },
            ],
        },
    ];

    const yieldOnCost = returnsData?.yieldOnCost ?? {};
    const marketCapRate = returnsData?.marketCapitalizationRate ?? {};
    const developmentSpread = returnsData?.developmentSpread ?? {};
    const projectProfit = returnsData?.projectProfit;
    const projectProfitMargin = returnsData?.projectProfitMargin;

    return [
        {
            id: 'property-returns-section',
            title: 'Property returns',
            columns: ['', 'ROI', 'EMx', 'IRR'],
            rows: [
                {
                    id: 'unlevered-row',
                    label: 'Unlevered',
                    values: [
                        { value: '' },
                        { value: unleveredRoi, format: 'percentage' },
                        { value: numeralFormatter(unleveredEmx, '0,0.00'), format: 'none', label: 'x' },
                        { value: unleveredIrr, format: 'percentage' },
                    ],
                },
                {
                    id: 'levered-row',
                    label: 'Levered',
                    values: [
                        { value: '' },
                        { value: leveredNetRoi, format: 'percentage' },
                        { value: numeralFormatter(leveredEmx, '0,0.00'), format: 'none', label: 'x' },
                        { value: leveredIrr, format: 'percentage' },
                    ],
                },
            ],
        },
        {
            id: 'partnership-returns-section',
            title: 'Partnership returns',
            columns: ['Net proceeds', 'ROI', 'EMx', 'IRR'],
            rows: partnershipRows,
        },
        {
            id: 'development-returns-section',
            title: 'Development returns',
            columns: ['', 'Untrended', 'Trended', 'Sale'],
            rows: [
                {
                    id: 'yield-on-cost-row',
                    label: 'Yield-on-cost',
                    values: [
                        { value: '', format: 'percentage' },
                        { value: yieldOnCost?.untrended ?? '-', format: 'percentage' },
                        { value: yieldOnCost?.trended ?? '-', format: 'percentage' },
                        { value: yieldOnCost?.sale ?? '-', format: 'percentage' },
                    ],
                },
                {
                    id: 'market-cap-rate-row',
                    label: 'Market cap rate',
                    values: [
                        { value: '', format: 'percentage' },
                        { value: marketCapRate?.current ?? '-', format: 'percentage' },
                        { value: marketCapRate?.trended ?? '-', format: 'percentage' },
                        { value: marketCapRate?.onSale ?? '-', format: 'percentage' },
                    ],
                },
                {
                    id: 'development-spread-row',
                    label: 'Development spread',
                    values: [
                        { value: '', format: 'percentage' },
                        {
                            value: typeof developmentSpread?.untrended === 'number'
                                ? numeralFormatter(developmentSpread.untrended, '0,0')
                                : '-',
                            format: 'none',
                            label: typeof developmentSpread.untrended === 'number' ? ' bps' : '',
                        },
                        {
                            value: typeof developmentSpread.trended === 'number'
                                ? numeralFormatter(developmentSpread.trended, '0,0')
                                : '-',
                            format: 'none',
                            label: typeof developmentSpread.trended === 'number' ? ' bps' : '',
                        },
                        {
                            value: typeof developmentSpread?.sale === 'number'
                                ? numeralFormatter(developmentSpread.sale, '0,0')
                                : '-',
                            format: 'none',
                            label: typeof developmentSpread.sale === 'number' ? ' bps' : '',
                        },
                    ],
                },
                {
                    id: 'project-profit-row',
                    label: 'Project profit',
                    tooltip: PROJECT_PROFIT_TOOLTIP,
                    values: [
                        { value: '', format: 'percentage' },
                        { value: '-', format: 'none' },
                        { value: '-', format: 'none' },
                        { value: projectProfit ?? '-', format: 'currency' },
                    ],
                },
                {
                    id: 'project-profit-margin-row',
                    label: 'Project profit margin',
                    tooltip: PROJECT_PROFIT_MARGIN_TOOLTIP,
                    values: [
                        { value: '', format: 'percentage' },
                        { value: '-', format: 'none' },
                        { value: '-', format: 'none' },
                        { value: projectProfitMargin ?? '-', format: 'percentage' },
                    ],
                },
            ],
        },
    ];
}

export function formatOperations(
    proFormaModelId: number,
    disposition: IDispositionData,
    incomeCalculationType: IncomeCalculationType,
    wattsType: WattsType,
): Section[] {
    if (!disposition) return [];

    return [
        formatCashFlows(disposition),
        formatNetOperatingIncome(disposition),
        formatCashFlowFromOperations(proFormaModelId, disposition, incomeCalculationType, wattsType),
    ];
}

function formatCashFlows(disposition: IDispositionData): Section {
    return {
        id: 'cash-flows-section',
        title: 'Cash flows',
        columns: ['Untrended', 'Trended', 'Sale'],
        rows: [
            {
                id: 'effective-gross-revenue',
                label: 'Effective gross revenue',
                values: [
                    { value: disposition.totalEffectiveGrossRevenue?.untrended, format: 'currency' },
                    { value: disposition.totalEffectiveGrossRevenue?.trended, format: 'currency' },
                    { value: disposition.totalEffectiveGrossRevenue?.sale, format: 'currency' },
                ],
            },
            {
                id: 'operating-expenses',
                label: 'Operating expenses',
                values: [
                    { value: disposition.totalOperatingExpenses?.untrended, format: 'currency', isNegative: true },
                    { value: disposition.totalOperatingExpenses?.trended, format: 'currency', isNegative: true },
                    { value: disposition.totalOperatingExpenses?.sale, format: 'currency', isNegative: true },
                ],
            },
        ],
    };
}

function formatNetOperatingIncome(disposition: IDispositionData): Section {
    return {
        id: 'net-operating-income-section',
        rows: [
            {
                id: 'net-operating-income',
                label: 'Net operating income',
                values: [
                    { value: disposition.netOperatingIncome?.untrended, format: 'currency' },
                    { value: disposition.netOperatingIncome?.trended, format: 'currency' },
                    { value: disposition.netOperatingIncome?.sale, format: 'currency' },
                ],
            },
            {
                id: 'capital-expenditures',
                label: 'Capital expenditures',
                values: [
                    { value: disposition.totalCapitalExpenditures?.untrended, format: 'currency', isNegative: true },
                    { value: disposition.totalCapitalExpenditures?.trended, format: 'currency', isNegative: true },
                    { value: disposition.totalCapitalExpenditures?.sale, format: 'currency', isNegative: true },
                ],
            },
        ],
    };
}

function formatCashFlowFromOperations(
    proFormaModelId: number,
    disposition: IDispositionData,
    incomeCalculationType: IncomeCalculationType,
    wattsType: WattsType,
): Section {
    const isApartment = [ProFormaModelIds.APARTMENT, ProFormaModelIds.APARTMENT_WITH_RETAIL].includes(proFormaModelId);
    const isIncomeCalculationTypeEstimate = incomeCalculationType === IncomeCalculationType.ESTIMATE;
    const areaType = isApartment || isIncomeCalculationTypeEstimate ? 'SF' : 'RSF';

    let valuationPerUnitRows: Row[] = [];

    const valuationValuesPerUnit: RowValue[] = [
        { value: disposition.stabilizedValuePerUnit?.untrended, format: 'currency' },
        { value: disposition.stabilizedValuePerUnit?.trended, format: 'currency' },
        { value: disposition.stabilizedValuePerUnit?.sale, format: 'currency' },
    ];

    const valuationValuesPerSf: RowValue[] = [
        { value: disposition.stabilizedValuePerSf?.untrended, format: 'currency' },
        { value: disposition.stabilizedValuePerSf?.trended, format: 'currency' },
        { value: disposition.stabilizedValuePerSf?.sale, format: 'currency' },
    ];

    if (proFormaModelId === ProFormaModelIds.APARTMENT) {
        valuationPerUnitRows = [{
            id: 'valuation-unit',
            label: 'Valuation / unit',
            values: valuationValuesPerUnit,
            className: 'pl-2',
        }];
    } else if (proFormaModelId === ProFormaModelIds.APARTMENT_WITH_RETAIL) {
        valuationPerUnitRows = [
            {
                id: 'valuation-unit',
                label: 'Valuation / unit',
                values: valuationValuesPerUnit,
                className: 'pl-2',
            },
            {
                id: 'valuation-sf',
                label: 'Valuation / SF',
                values: valuationValuesPerSf,
                className: 'pl-2',
            },
        ];
    } else if ([ProFormaModelIds.OFFICE, ProFormaModelIds.RETAIL, ProFormaModelIds.INDUSTRIAL].includes(proFormaModelId)) {
        valuationPerUnitRows = [{
            id: 'valuation-sf',
            label: `Valuation / ${areaType}`,
            values: valuationValuesPerSf,
            className: 'pl-2',
        }];
    } else if (proFormaModelId === ProFormaModelIds.DATA_CENTER) {
        if (wattsType === WattsType.MEGAWATTS) {
            valuationPerUnitRows = [{
                id: 'valuation-mw',
                label: 'Valuation / MW',
                values: [
                    { value: disposition.stabilizedValuePerMw?.untrended, format: 'currency' },
                    { value: disposition.stabilizedValuePerMw?.trended, format: 'currency' },
                    { value: disposition.stabilizedValuePerMw?.sale, format: 'currency' },
                ],
                className: 'pl-2',
            }];
        } else {
            valuationPerUnitRows = [{
                id: 'valuation-kw',
                label: 'Valuation / kW',
                values: [
                    { value: disposition.stabilizedValuePerKw?.untrended, format: 'currency' },
                    { value: disposition.stabilizedValuePerKw?.trended, format: 'currency' },
                    { value: disposition.stabilizedValuePerKw?.sale, format: 'currency' },
                ],
                className: 'pl-2',
            }];
        }
    } else if (proFormaModelId === ProFormaModelIds.STUDENT_HOUSING) {
        const valuationValuesPerBed: RowValue[] = [
            { value: disposition.stabilizedValuePerBed?.untrended, format: 'currency' },
            { value: disposition.stabilizedValuePerBed?.trended, format: 'currency' },
            { value: disposition.stabilizedValuePerBed?.sale, format: 'currency' },
        ];
        valuationPerUnitRows = [
            { id: 'valuation-sf', label: 'Valuation / SF', values: valuationValuesPerSf, className: 'pl-2' },
            { id: 'valuation-unit', label: 'Valuation / unit', values: valuationValuesPerUnit, className: 'pl-2' },
            { id: 'valuation-bed', label: 'Valuation / bed', values: valuationValuesPerBed, className: 'pl-2' },
        ];
    }    

    return {
        id: 'cash-flow-from-operations-section',
        rows: [
            {
                id: 'cash-flow-operations',
                label: 'Cash flow from operations',
                values: [
                    { value: disposition.cashFlowFromOperations?.untrended, format: 'currency' },
                    { value: disposition.cashFlowFromOperations?.trended, format: 'currency' },
                    { value: disposition.cashFlowFromOperations?.sale, format: 'currency' },
                ],
            },
            {
                id: 'cap-rate',
                label: 'Cap rate',
                values: [
                    { value: disposition.marketCapitalizationRate?.current, format: 'percentage' },
                    { value: disposition.marketCapitalizationRate?.trended, format: 'percentage' },
                    { value: disposition.marketCapitalizationRate?.onSale, format: 'percentage' },
                ],
            },
            {
                id: 'valuation',
                label: 'Valuation',
                values: [
                    { value: disposition.stabilizedValue?.untrended, format: 'currency' },
                    { value: disposition.stabilizedValue?.trended, format: 'currency' },
                    { value: disposition.stabilizedValue?.sale, format: 'currency' },
                ],
            },
            {
                id: 'percent-of-total-project-cost',
                label: '% of total project cost',
                values: [
                    { value: disposition.percentOfTotalProjectCost?.untrended, format: 'percentage' },
                    { value: disposition.percentOfTotalProjectCost?.trended, format: 'percentage' },
                    { value: disposition.percentOfTotalProjectCost?.sale, format: 'percentage' },
                ],
                className: 'pl-2',
            },
            ...valuationPerUnitRows,
        ],
    };
}

// ============================================================================
// Land Development executive summary
// ============================================================================

export const getTotalLotCount = (lotMixes: IResidentialLotMix[]): number =>
    lotMixes.reduce((sum, lot) => sum + (lot.lotCount || 0), 0);

export const getTotalFrontFootage = (lotMixes: IResidentialLotMix[]): number =>
    lotMixes.reduce((sum, lot) => sum + (lot.lotWidth || 0) * (lot.lotCount || 0), 0);

export const getLotMixString = (lotMixes: IResidentialLotMix[]): string =>
    lotMixes.map(lot => `${lot.lotCount || 0} × ${lot.lotWidth || 0}ft`).join(', ');

export function formatLandDevelopmentAssumptions(
    reportMetrics: IReportMetrics | undefined,
    disposition: IDispositionData | undefined,
    totalInitialTake: number | null | undefined,
    totalRecurringNum: number | null | undefined,
): Section[] {
    let interestRate: RowValue;
    if (reportMetrics?.interestRateType === LOAN_RATE_TYPE.VARIABLE) {
        interestRate = { value: 'Variable', format: 'none' };
    } else if (reportMetrics?.interestRateType === LOAN_RATE_TYPE.FIXED && reportMetrics?.interestRate != null) {
        interestRate = { value: reportMetrics.interestRate, format: 'percentage' };
    } else {
        interestRate = { value: '-', format: 'none' };
    }

    const recurringLabel = `Recurring / ${disposition?.saleFrequency === 'monthly' ? 'Mo' : 'Qtr'}`;

    return [
        {
            id: 'land-dev-assumptions-pricing',
            columns: ['Value'],
            rows: [
                {
                    id: 'avg-lot-price',
                    label: 'Avg lot price ($/FF)',
                    values: [{ value: reportMetrics?.weightedAvgLotPricePerFrontFoot || '-', format: 'currency-cents' }],
                },
                {
                    id: 'annual-price-escalation',
                    label: 'Annual price escalation',
                    values: [{ value: disposition?.annualPriceEscalationPercentage ?? '-', format: 'percentage' }],
                },
                { id: 'closing-costs', label: 'Closing costs %', values: [{ value: disposition?.closingCostsPercentage ?? '-', format: 'percentage' }] },
            ],
        },
        {
            id: 'land-dev-assumptions-financing',
            columns: ['Value'],
            rows: [
                { id: 'ad-loan-to-cost', label: 'A&D loan-to-cost', values: [{ value: reportMetrics?.adLoanToCost ?? '-', format: 'percentage' }] },
                { id: 'interest-rate', label: 'Interest rate', values: [interestRate] },
                {
                    id: 'financing-fee',
                    label: 'Financing fee',
                    values: [{ value: reportMetrics?.financingFeePercentage ?? '-', format: 'percentage' }],
                },
            ],
        },
        {
            id: 'land-dev-assumptions-takedown',
            columns: ['Value'],
            rows: [
                { id: 'initial-take', label: 'Initial take', values: [{ value: totalInitialTake ?? '-', format: 'none' }] },
                { id: 'recurring', label: recurringLabel, values: [{ value: totalRecurringNum ?? '-', format: 'none' }] },
            ],
        },
    ];
}

export function formatLandDevelopmentForecast(
    reportMetrics: IReportMetrics | undefined,
    returnsData: IProFormaReturnsData | undefined,
): Section[] {
    const totalSaleProceeds = returnsData?.totalSaleProceeds ?? '-';
    const netSaleProceeds = returnsData?.netSaleProceedsAfterClosingCosts ?? '-';
    const otherRevenue = returnsData?.totalOtherRevenue ?? '-';
    const equityProceeds = returnsData?.totalEquityProceedFromCapitalEvents ?? '-';

    return [
        {
            id: 'land-dev-forecast-proceeds',
            columns: ['Value'],
            rows: [
                { id: 'total-sale-proceeds', label: 'Total sale proceeds (gross)', values: [{ value: totalSaleProceeds, format: 'currency' }] },
                { id: 'net-sale-proceeds', label: 'Net sale proceeds', values: [{ value: netSaleProceeds, format: 'currency' }] },
                { id: 'other-revenue', label: 'Other revenue', values: [{ value: otherRevenue, format: 'currency' }] },
                {
                    id: 'ad-loan-payoff',
                    label: 'A&D loan payoff',
                    values: [{ value: reportMetrics?.totalConstructionDebt || '-', format: 'currency', isNegative: true }],
                },
                { id: 'equity-proceeds-sellout', label: 'Equity proceeds from sellout', values: [{ value: equityProceeds, format: 'currency' }] },
            ],
        },
        {
            id: 'land-dev-forecast-metrics',
            columns: ['Value'],
            rows: [
                { id: 'profit-per-lot', label: 'Profit per lot', values: [{ value: reportMetrics?.profitPerLot ?? '-', format: 'currency' }] },
                {
                    id: 'breakeven-lot-price',
                    label: 'Breakeven lot price ($/FF)',
                    values: [{ value: reportMetrics?.breakevenLotPricePerFrontFoot ?? '-', format: 'currency-cents' }],
                },
            ],
        },
    ];
}

export function formatLandDevelopmentReturns(
    returnsData: IProFormaReturnsData | undefined,
    waterfallAnalysis: IWaterfallAnalysis | undefined,
): Section[] {
    if (!returnsData || Object.keys(returnsData).length === 0) {
        return [];
    }

    const unleveredEmx = returnsData.netUnleveredEquityMultiple;
    const leveredEmx = returnsData.netLeveredEquityMultiple;
    const unleveredRoi = unleveredEmx != null ? Number(unleveredEmx) - 1 : '-';
    const leveredRoi = leveredEmx != null ? Number(leveredEmx) - 1 : '-';

    interface IPropertyRowArgs {
        id: string;
        label: string;
        profit: RowValue['value'];
        roi: RowValue['value'];
        emx: RowValue['value'] | null | undefined;
        irr: RowValue['value'];
    }

    const propertyRow = ({ id, label, profit, roi, emx, irr }: IPropertyRowArgs): Row => ({
        id,
        label,
        values: [
            { value: profit ?? '-', format: 'currency' },
            { value: roi, format: 'percentage' },
            { value: numeralFormatter(emx ?? '-', '0,0.00'), format: 'none', label: 'x' },
            { value: irr ?? '-', format: 'percentage' },
        ],
    });

    const summary = waterfallAnalysis?.waterfallSummary;
    const partnerRow = (
        id: string,
        name: string | undefined,
        fallbackName: string,
        netProfit: RowValue['value'],
        roi: RowValue['value'],
        emx: RowValue['value'] | null | undefined,
        irr: RowValue['value'],
    ): Row => ({
        id,
        label: name || fallbackName,
        values: [
            { value: netProfit, format: 'currency' },
            { value: roi, format: 'percentage' },
            { value: numeralFormatter(emx ?? undefined, '0,0.00'), format: 'none', label: 'x' },
            { value: irr, format: 'percentage' },
        ],
    });

    return [
        {
            id: 'land-dev-property-returns',
            title: 'Property returns',
            columns: ['Total profit', 'ROI', 'EMx', 'IRR'],
            rows: [
                propertyRow({
                    id: 'unlevered-row',
                    label: 'Unlevered',
                    profit: returnsData.netUnleveredNetProfit,
                    roi: unleveredRoi,
                    emx: unleveredEmx,
                    irr: returnsData.netUnleveredIrr,
                }),
                propertyRow({
                    id: 'levered-row',
                    label: 'Levered',
                    profit: returnsData.netLeveredNetProfit,
                    roi: leveredRoi,
                    emx: leveredEmx,
                    irr: returnsData.netLeveredIrr,
                }),
            ],
        },
        {
            id: 'land-dev-partnership-returns',
            title: 'Partnership returns',
            columns: ['Net proceeds', 'ROI', 'EMx', 'IRR'],
            rows: [
                partnerRow('lp', summary?.lpName, 'Limited Partner', summary?.lpNetProfit, summary?.lpRoi, summary?.lpEmx, summary?.lpIrr),
                partnerRow('gp1', summary?.gp1Name, 'General Partner', summary?.gp1NetProfit, summary?.gp1Roi, summary?.gp1Emx, summary?.gp1Irr),
            ],
        },
    ];
}

export function formatLandDevelopmentDispositionSchedule(
    reportMetrics: IReportMetrics | undefined,
    disposition: IDispositionData | undefined,
): Section[] {
    const periods = reportMetrics?.dispositionSchedule ?? [];
    if (periods.length === 0) {
        return [];
    }

    const isMonthly = disposition?.saleFrequency === 'monthly';

    const rows: Row[] = periods.map(period => ({
        id: `disposition-period-${period.period}`,
        label: `${isMonthly ? 'M' : 'Q'}${period.period}`,
        values: [
            { value: period.lotsSold ?? '-', format: 'none' },
            { value: period.cumulativeLots ?? '-', format: 'none' },
            { value: period.netRevenue ?? '-', format: 'currency' },
            { value: period.adPaydown ?? 0, format: 'currency', isNegative: true },
            { value: period.equityProceeds ?? '-', format: 'currency' },
            { value: period.loanOutstanding ?? '-', format: 'currency' },
        ],
    }));

    return [
        {
            id: 'land-dev-disposition-schedule',
            title: isMonthly ? 'Month' : 'Quarter',
            columns: ['Lots Sold', 'Cumul. Lots', 'Net Revenue', 'A&D Paydown', 'Equity Proceeds', 'Loan Outstanding'],
            rows,
        },
        {
            id: 'land-dev-disposition-total',
            isTotal: true,
            rows: [{
                id: 'disposition-total',
                label: 'Total',
                values: [
                    { value: reportMetrics?.totalLotsSold ?? '-', format: 'none' },
                    { value: reportMetrics?.totalLotsSold ?? '-', format: 'none' },
                    { value: reportMetrics?.totalNetRevenue ?? '-', format: 'currency' },
                    { value: reportMetrics?.totalAdPaydown ?? 0, format: 'currency', isNegative: true },
                    { value: reportMetrics?.totalEquityProceeds ?? '-', format: 'currency' },
                    { value: reportMetrics?.finalLoanOutstanding ?? '-', format: 'currency' },
                ],
            }],
        },
    ];
}
